import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { get } from '../../../api/client';
import type { OrderFilters } from '../../../types';
import {
  DEFAULT_ORDER_STATUS_SEGMENT,
  ORDER_STATUS_SEGMENTS,
  isDefaultOrderStatusSegment,
  orderStatusSegmentOf,
  type OrderStatusSegment,
} from '../../../utils/orderStatusFilter';
import { SERVICE_TYPE_ICON, serviceTypeChipClass } from '../../../utils/serviceTypeColor';
import { PAYMENT_STATUSES, PAYMENT_STATUS_FILTER_LABELS } from '../constants';
import {
  DEFAULT_PERIOD_KIND,
  PERIOD_KINDS,
  customPeriodOf,
  isRangeReversed,
  periodsFromBuckets,
  type ExportMonthBucket,
  type PeriodKind,
} from '../utils/exportPeriod';
import type { ExportSelection } from '../utils/exportOrders';

interface ServiceType {
  id: string;
  name: string;
}

interface Props {
  filters: OrderFilters;
  onChange: (f: OrderFilters) => void;
  total: number;
  onExport: (format: 'xlsx' | 'pdf' | 'print', options?: ExportSelection) => Promise<void>;
  /**
   * `filters.customerId` 那格对应的客户姓名，由订单页从深链带进来。
   * 只用于把 chip 写得出人话（「客户：李庆伟」而不是一个 uuid）与导出文件名，不参与取数。
   */
  customerName?: string;
}

const STATUS_DOT: Record<string, string> = {
  '未执行': 'bg-amber-400',
  '执行中': 'bg-cyan-400',
  '已完成': 'bg-teal-400',
  '已取消': 'bg-rose-400',
  '已归档': 'bg-gray-400',
};

/** 只有对得上单一状态的分段才有点可画：「在途」横跨两档，给它配哪种颜色都是假信息。 */
function segmentDot(seg: OrderStatusSegment): string | null {
  return seg.statuses?.length === 1 ? (STATUS_DOT[seg.statuses[0]] ?? null) : null;
}

// 服务类型 chip 的取色与取图标见 utils/serviceTypeColor.ts（名字是自由文本，不做名字表）。

export default function OrderFiltersBar({ filters, onChange, total, onExport, customerName }: Props) {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const [serviceTypes, setServiceTypes] = useState<ServiceType[]>([]);
  // 期次默认落在「按月 + 最近一个有账的月份」，付款状态默认「跟随筛选」（列表本来就带着它）。
  const [periodKind, setPeriodKind] = useState<PeriodKind>(DEFAULT_PERIOD_KIND);
  const [periodIndex, setPeriodIndex] = useState(0);
  const [paymentChoice, setPaymentChoice] = useState('');
  // 自定义档的两个日期框：进入该档时从列表当前区间抄一份作起点，此后归面板自己管。
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  // 候选期次的来源：服务端「哪些月份有账」。只在打开导出面板那一刻拉，
  // 每次打开都重拉一次——中途新下或删了单，候选和单量得跟着变，这份数不能缓存过夜。
  const [months, setMonths] = useState<ExportMonthBucket[]>([]);
  const [monthsState, setMonthsState] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle');

  useEffect(() => {
    get<ServiceType[]>('/service-types').then(data => {
      if (data) {
        setServiceTypes(data);
      }
    }).catch(error => {
      console.error('获取服务类型失败:', error);
    });
  }, []);

  const loadPeriods = async () => {
    setMonthsState('loading');
    try {
      const res = await get<{ months: ExportMonthBucket[] }>('/orders/export/periods');
      setMonths(res?.months ?? []);
      setMonthsState('ready');
      setPeriodIndex(0);
    } catch {
      setMonthsState('error');
    }
  };

  const toggleExportMenu = () => {
    const next = !exportMenuOpen;
    setExportMenuOpen(next);
    if (next) void loadPeriods();
  };

  const update = (patch: Partial<OrderFilters>) => onChange({ ...filters, ...patch });

  // 高亮跟着「归一化后的分段」走，而不是跟着原始串比：状态里万一落了个陌生值，
  // 取数按默认分段走（见 orderStatusSegmentOf），按钮也必须亮在同一档上。
  const currentSegment = orderStatusSegmentOf(filters.orderStatus);

  const getMonthRange = (offset: number) => {
    const now = new Date();
    const date = new Date(now.getFullYear(), now.getMonth() + offset, 1);
    const from = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-01`;
    const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
    const to = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
    return { from, to };
  };

  const isMonthActive = (offset: number) => {
    const { from, to } = getMonthRange(offset);
    return filters.dateFrom === from && filters.dateTo === to;
  };

  const toggleMonth = (offset: number) => {
    if (isMonthActive(offset)) {
      update({ dateFrom: '', dateTo: '' });
    } else {
      const { from, to } = getMonthRange(offset);
      update({ dateFrom: from, dateTo: to });
    }
  };

  // 「在途」是列表的常态视图而不是筛选：算进计数就会一进门挂个「1」，
  // 而「清除筛选」永远清不掉它。
  const activeCount = [
    isDefaultOrderStatusSegment(filters.orderStatus) ? '' : filters.orderStatus,
    filters.paymentStatus,
    filters.dateFrom,
    filters.dateTo,
    filters.serviceType,
    filters.customerId,
    filters.upcoming ? 'upcoming' : '',
  ].filter(Boolean).length;

  const clearAll = () => onChange({
    keyword: filters.keyword,
    orderStatus: DEFAULT_ORDER_STATUS_SEGMENT,
    paymentStatus: '',
    dateFrom: '',
    dateTo: '',
    serviceType: '',
    // 客户维度是「这位客户的全部订单」这个来意本身，不是可留可去的条件：
    // 留着它，清除筛选之后列表仍只剩一个人的单，而那正是用户以为已经清掉的东西。
    customerId: '',
    upcoming: false,
  });

  // 候选由服务端那份「有账的月份」派生：档位一换就重算一遍，索引越界一律回落最近一期。
  const periods = periodsFromBuckets(periodKind, months);
  const safeIndex = periodIndex < periods.length ? periodIndex : 0;
  const reversedRange = periodKind === 'custom' && isRangeReversed(customFrom, customTo);
  // 自定义档一旦选中就**接管**日期维度（两头都空＝不限日期，也要把列表的区间覆盖掉），
  // 所以它恒给一个对象；月/季/年档在读不到候选时给 null，让这一次导出退回列表筛选。
  const selectedPeriod =
    periodKind === 'custom'
      ? customPeriodOf(customFrom, customTo)
      : periods.length > 0
        ? periods[safeIndex]
        : null;

  // 拦在点击之前：期次没读到就放过去，用户以为按某一期导的、实际拿回的是列表全量，
  // 这种「看着成功其实取错数」比报个错难查得多（读取失败也绝不静默当成「没有账」）。
  const exportBlockedReason = reversedRange
    ? '自定义时间段的起止填反了'
    : periodKind !== 'custom' && monthsState === 'error'
      ? '期次读取失败，点上面的重试或改用自定义'
      : '';

  const pickPeriodKind = (kind: PeriodKind) => {
    setPeriodKind(kind);
    setPeriodIndex(0);
    if (kind === 'custom') {
      setCustomFrom(filters.dateFrom ?? '');
      setCustomTo(filters.dateTo ?? '');
    }
  };

  const chipCls = (isActive: boolean): string =>
    `px-2.5 py-1 text-xs rounded-lg border transition-all cursor-pointer whitespace-nowrap ${
      isActive ? 'bg-teal-500 text-white border-teal-500' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
    }`;

  const handleExport = async (format: 'xlsx' | 'pdf' | 'print') => {
    if (exportBlockedReason) return;
    setExporting(true);
    setExportMenuOpen(false);
    try {
      await onExport(format, {
        period: selectedPeriod ?? undefined,
        paymentStatus: paymentChoice || undefined,
      });
    } finally { setExporting(false); }
  };

  return (
    <div className="bg-white border border-gray-100 rounded-xl">
      {/* Mobile quick status pills */}
      <div className="sm:hidden px-3 pt-3 pb-0">
        <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
          {ORDER_STATUS_SEGMENTS.map(seg => {
            const isActive = currentSegment.value === seg.value;
            const dot = segmentDot(seg);
            return (
              <button
                key={seg.value || 'all'}
                onClick={() => update({ orderStatus: seg.value })}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap shrink-0 cursor-pointer transition-all border ${
                  isActive
                    ? 'bg-teal-500 text-white border-teal-500'
                    : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100'
                }`}
              >
                {dot && <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />}
                {seg.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="p-3 sm:p-4">
        {/* Top row: search + buttons */}
        <div className="flex items-center gap-2">
          {/* Search */}
          <div className="flex-1 relative">
            <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
              <i className="ri-search-line text-sm text-gray-400" />
            </div>
            <input
              type="text"
              value={filters.keyword}
              onChange={e => update({ keyword: e.target.value })}
              placeholder="搜索订单号、客户姓名、联系人..."
              className="w-full pl-9 pr-4 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-400 transition-all placeholder:text-gray-400"
            />
          </div>

          {/* 即将到来 quick filter */}
          <button
            onClick={() => update({ upcoming: !filters.upcoming, dateFrom: '', dateTo: '' })}
            title="显示未来 7 天的活动订单"
            className={`flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border transition-all whitespace-nowrap cursor-pointer ${
              filters.upcoming
                ? 'bg-teal-500 text-white border-teal-500'
                : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'
            }`}
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-calendar-event-line text-sm" />
            </div>
            <span className="hidden sm:inline">即将到来</span>
          </button>

          {/* Export button with dropdown */}
          <div className="relative">
            <button
              onClick={toggleExportMenu}
              disabled={exporting}
              title="导出订单"
              className="flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100 transition-all whitespace-nowrap cursor-pointer disabled:opacity-50"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                {exporting
                  ? <i className="ri-loader-4-line animate-spin text-sm" />
                  : <i className="ri-download-2-line text-sm" />}
              </div>
              <span className="hidden sm:inline">导出</span>
              <i className="ri-arrow-down-s-line text-xs text-gray-400" />
            </button>
            {exportMenuOpen && (
              <div className="absolute right-0 top-full mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-20 p-3 space-y-3">
                {/* 导出周期 */}
                <div>
                  <label className="text-xs font-medium text-gray-500 mb-1.5 block">导出周期</label>
                  <div className="flex flex-wrap gap-1.5">
                    {PERIOD_KINDS.map(kind => (
                      <button
                        key={kind.value}
                        onClick={() => pickPeriodKind(kind.value)}
                        className={chipCls(periodKind === kind.value)}
                      >{kind.label}</button>
                    ))}
                  </div>
                  {periodKind === 'custom' ? (
                    <div className="mt-2 flex items-center gap-2">
                      <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)}
                        className="flex-1 min-w-0 px-2 py-1.5 text-xs bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-400 text-gray-700" />
                      <span className="text-gray-400 text-xs shrink-0">至</span>
                      <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)}
                        className="flex-1 min-w-0 px-2 py-1.5 text-xs bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-400 text-gray-700" />
                    </div>
                  ) : periods.length > 0 ? (
                    <select
                      value={safeIndex}
                      onChange={e => setPeriodIndex(Number(e.target.value))}
                      className="mt-2 w-full px-2 py-1.5 text-xs bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-400 text-gray-700 cursor-pointer"
                    >
                      {periods.map((p, i) => (
                        <option key={p.label} value={i}>{p.label} · {p.orderCount} 单</option>
                      ))}
                    </select>
                  ) : null}
                  {/* 三种就地状态：读数中 / 读失败（给重试，绝不静默当成「没有账」）/ 真的没账。
                      没有候选时**不渲染空的 select 壳**——一个没有任何选项的下拉看着就是控件坏了。 */}
                  {periodKind !== 'custom' && monthsState === 'loading' && (
                    <p className="mt-1.5 text-[11px] text-gray-400">正在读取有账的期次…</p>
                  )}
                  {periodKind !== 'custom' && monthsState === 'error' && (
                    <p className="mt-1.5 text-[11px] text-rose-500 flex items-center gap-1.5">
                      <button onClick={() => void loadPeriods()} className="underline cursor-pointer">重试读取期次</button>
                      <button onClick={() => pickPeriodKind('custom')} className="underline cursor-pointer">或改用自定义</button>
                    </p>
                  )}
                  {periodKind !== 'custom' && monthsState === 'ready' && periods.length === 0 && (
                    <p className="mt-1.5 text-[11px] text-gray-400 flex items-center gap-1.5">
                      <span>还没有可导出的订单，</span>
                      <button onClick={() => pickPeriodKind('custom')} className="underline cursor-pointer">改用自定义时间段</button>
                    </p>
                  )}
                  {reversedRange && (
                    <p className="mt-1.5 text-[11px] text-rose-500">起止日期填反了，导出会得到空表</p>
                  )}
                </div>

                {/* 付款状态 */}
                <div>
                  <label className="text-xs font-medium text-gray-500 mb-1.5 block">付款状态</label>
                  <div className="flex flex-wrap gap-1.5">
                    <button onClick={() => setPaymentChoice('')} className={chipCls(!paymentChoice)}>跟随筛选</button>
                    {PAYMENT_STATUSES.map(s => (
                      <button
                        key={s}
                        onClick={() => setPaymentChoice(paymentChoice === s ? '' : s)}
                        className={chipCls(paymentChoice === s)}
                      >{PAYMENT_STATUS_FILTER_LABELS[s] ?? s}</button>
                    ))}
                  </div>
                </div>

                {/* 期次的落点是活动日期（档期），不是创建时间；后端没有按创建时间区间筛选的入参。
                    这两格只改这一次导出的取数，列表本身的筛选一个字不动。 */}
                <p className="text-[11px] leading-relaxed text-gray-400">
                  期次按活动日期取，只列有账的期；自定义两头都空＝不限日期。只改这一次导出，列表筛选不受影响
                </p>

                <div className="pt-2 border-t border-gray-100 space-y-1">
                  {/* 三个出口并列，选哪条都由面板这一份期次/付款状态决定取数：
                      Excel 是给机器读的全量台账，PDF 是能直接存档转发的文件，
                      打印那条只把表交给浏览器打印对话框（选纸张、份数、双面只有它能做）。
                      前两个走服务端产物，第三个走同一份数据的可打印 HTML。 */}
                  <button
                    onClick={() => handleExport('xlsx')}
                    disabled={!!exportBlockedReason}
                    className="w-full flex items-center gap-2 px-2 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <i className="ri-file-excel-line text-teal-500 text-sm" />
                    导出 Excel
                  </button>
                  <button
                    onClick={() => handleExport('pdf')}
                    disabled={!!exportBlockedReason}
                    className="w-full flex items-center gap-2 px-2 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <i className="ri-file-pdf-line text-rose-500 text-sm" />
                    导出 PDF
                  </button>
                  <button
                    onClick={() => handleExport('print')}
                    disabled={!!exportBlockedReason}
                    className="w-full flex items-center gap-2 px-2 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <i className="ri-printer-line text-gray-400 text-sm" />
                    打印
                  </button>
                  {exportBlockedReason && (
                    <p className="text-[11px] text-rose-500">{exportBlockedReason}</p>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Filter toggle */}
          <button
            onClick={() => setExpanded(v => !v)}
            className={`flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border transition-all whitespace-nowrap cursor-pointer ${
              expanded || activeCount > 0
                ? 'bg-teal-50 text-teal-600 border-teal-200'
                : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'
            }`}
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-filter-3-line text-sm" />
            </div>
            <span className="hidden sm:inline">筛选</span>
            {activeCount > 0 && (
              <span className="w-4 h-4 rounded-full bg-teal-500 text-white text-[10px] flex items-center justify-center font-medium">
                {activeCount}
              </span>
            )}
          </button>

          {/* Total badge - desktop only */}
          <span className="hidden sm:flex shrink-0 items-center text-xs text-gray-400 whitespace-nowrap">
            共 <strong className="text-gray-700 mx-1">{total}</strong> 条
          </span>
        </div>

        {/* 客户维度：从客户档案页「查看全部订单」带进来的那一条，就地可摘，也可回档案页 */}
        {filters.customerId && (
          <div className="mt-2 flex items-center gap-2">
            <span className="flex items-center gap-1.5 pl-2.5 pr-1 py-1 text-xs rounded-full bg-teal-50 border border-teal-200 text-teal-700 max-w-full">
              <i className="ri-user-line text-xs shrink-0" />
              <button
                onClick={() => navigate(`/customers/${filters.customerId}`)}
                title="回到该客户档案"
                className="cursor-pointer hover:underline truncate max-w-[12rem]"
              >
                客户：{customerName || '该客户'}
              </button>
              <button
                onClick={() => update({ customerId: '' })}
                title="取消按客户筛选"
                className="w-5 h-5 flex items-center justify-center rounded-full text-teal-500 hover:bg-teal-100 hover:text-teal-700 cursor-pointer shrink-0"
              >
                <i className="ri-close-line text-xs" />
              </button>
            </span>
          </div>
        )}

        {/* Mobile total count */}
        <div className="sm:hidden mt-2 flex items-center justify-between">
          <span className="text-xs text-gray-400">共 <strong className="text-gray-700">{total}</strong> 条订单</span>
          {activeCount > 0 && (
            <button onClick={clearAll} className="text-xs text-rose-400 hover:text-rose-600 cursor-pointer transition-colors">
              清除筛选
            </button>
          )}
        </div>

        {/* Expandable filters */}
        {expanded && (
          <div className="mt-4 pt-4 border-t border-gray-100 space-y-4">

            {/* ── 服务类型 ── */}
            {serviceTypes.length > 0 && (
              <div>
                <label className="text-xs font-medium text-gray-500 mb-2 block">服务类型</label>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => update({ serviceType: '' })}
                    className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap cursor-pointer transition-all border ${
                      !filters.serviceType
                        ? 'bg-teal-500 text-white border-teal-500'
                        : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    全部
                  </button>
                  {serviceTypes.map(st => {
                    const colorCls = serviceTypeChipClass(st.name ?? '');
                    const isActive = filters.serviceType === st.name;
                    return (
                      <button
                        key={st.id}
                        onClick={() => update({ serviceType: isActive ? '' : (st.name ?? '') })}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap cursor-pointer transition-all border ${
                          isActive
                            ? 'bg-teal-500 text-white border-teal-500'
                            : `${colorCls} hover:opacity-80`
                        }`}
                      >
                        <i className={`${SERVICE_TYPE_ICON} text-xs`} />
                        {st.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Order status */}
              <div>
                <label className="text-xs font-medium text-gray-500 mb-2 block">订单状态</label>
                <div className="flex flex-wrap gap-2">
                  {ORDER_STATUS_SEGMENTS.map(seg => {
                    const isActive = currentSegment.value === seg.value;
                    return (
                      <button key={seg.value || 'all'}
                        onClick={() => update({ orderStatus: isActive ? DEFAULT_ORDER_STATUS_SEGMENT : seg.value })}
                        className={`px-3 py-1.5 text-xs rounded-lg border transition-all cursor-pointer whitespace-nowrap ${
                          isActive ? 'bg-teal-500 text-white border-teal-500' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                        }`}
                      >{seg.label}</button>
                    );
                  })}
                </div>
              </div>

              {/* Payment status */}
              <div>
                <label className="text-xs font-medium text-gray-500 mb-2 block">付款状态</label>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => update({ paymentStatus: '' })}
                    className={`px-3 py-1.5 text-xs rounded-lg border transition-all cursor-pointer whitespace-nowrap ${
                      !filters.paymentStatus ? 'bg-teal-500 text-white border-teal-500' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                    }`}
                  >全部</button>
                  {PAYMENT_STATUSES.map(s => (
                    <button key={s}
                      onClick={() => update({ paymentStatus: filters.paymentStatus === s ? '' : s })}
                      className={`px-3 py-1.5 text-xs rounded-lg border transition-all cursor-pointer whitespace-nowrap ${
                        filters.paymentStatus === s ? 'bg-teal-500 text-white border-teal-500' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                      }`}
                    >{PAYMENT_STATUS_FILTER_LABELS[s] ?? s}</button>
                  ))}
                </div>
              </div>
            </div>

            {/* Date range */}
            <div>
              <label className="text-xs font-medium text-gray-500 mb-2 block">订单日期范围</label>
              {/* 快捷月份 */}
              <div className="flex items-center gap-2 mb-2.5">
                {[
                  { label: '上月', offset: -1, icon: 'ri-arrow-left-s-line' },
                  { label: '本月', offset: 0,  icon: 'ri-calendar-2-line'   },
                  { label: '下月', offset: 1,  icon: 'ri-arrow-right-s-line' },
                ].map(({ label, offset, icon }) => (
                  <button
                    key={label}
                    onClick={() => toggleMonth(offset)}
                    className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap cursor-pointer transition-all border ${
                      isMonthActive(offset)
                        ? 'bg-teal-500 text-white border-teal-500'
                        : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <div className="w-3.5 h-3.5 flex items-center justify-center">
                      <i className={`${icon} text-xs`} />
                    </div>
                    {label}
                  </button>
                ))}
                {(filters.dateFrom || filters.dateTo) && (
                  <span className="text-xs text-gray-400 ml-1">
                    {filters.dateFrom && filters.dateTo
                      ? `${filters.dateFrom.slice(5)} ~ ${filters.dateTo.slice(5)}`
                      : filters.dateFrom
                        ? `${filters.dateFrom.slice(5)} 起`
                        : `至 ${filters.dateTo?.slice(5)}`
                    }
                  </span>
                )}
              </div>
              {/* 自定义日期输入 */}
              <div className="flex items-center gap-3">
                <input type="date" value={filters.dateFrom} onChange={e => update({ dateFrom: e.target.value })}
                  className="flex-1 px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-400 transition-all text-gray-700" />
                <span className="text-gray-400 text-sm shrink-0">至</span>
                <input type="date" value={filters.dateTo} onChange={e => update({ dateTo: e.target.value })}
                  className="flex-1 px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-400 transition-all text-gray-700" />
              </div>
            </div>

            {activeCount > 0 && (
              <div className="flex justify-end">
                <button onClick={clearAll}
                  className="text-xs text-gray-400 hover:text-rose-500 transition-colors cursor-pointer flex items-center gap-1">
                  <div className="w-3 h-3 flex items-center justify-center"><i className="ri-close-line text-xs" /></div>
                  清除筛选
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
